import moment from "moment";

export interface UnifiedEntry {
  id: string;
  date: string;
  type: "attendance" | "weekend" | "holiday" | "leave" | "absent" | "future";
  original?: any;
}

export function calculateDuration(inTime?: string, outTime?: string): string {
  if (!inTime || !outTime) return "";
  try {
    const start = moment(inTime, [moment.ISO_8601, "hh:mm A", "HH:mm:ss", "HH:mm"]);
    const end = moment(outTime, [moment.ISO_8601, "hh:mm A", "HH:mm:ss", "HH:mm"]);
    if (!start.isValid() || !end.isValid()) return "";
    const durationMinutes = end.diff(start, "minutes");
    if (durationMinutes <= 0) return "";
    const hours = Math.floor(durationMinutes / 60);
    const mins = durationMinutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  } catch {
    return "";
  }
}

export function getDatesForFullMonth(month: number, year: number): string[] {
  const daysInMonth = new Date(year, month, 0).getDate();
  const dates: string[] = [];

  for (let day = 1; day <= daysInMonth; day++) {
    const dd = String(day).padStart(2, "0");
    const mm = String(month).padStart(2, "0");
    dates.push(`${year}-${mm}-${dd}`);
  }

  return dates;
}

export function getLeaveDates(leaveConfig: any, month: number, year: number): string[] {
  if (!leaveConfig) return [];
  const dayMap: { [key: string]: number } = {
    sun_day: 0,
    mon_day: 1,
    tues_day: 2,
    wednes_day: 3,
    thurs_day: 4,
    fri_day: 5,
    satur_day: 6,
  };

  const result: string[] = [];
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  Object.entries(dayMap).forEach(([key, weekday]) => {
    const value = leaveConfig[key];
    if (value === null || value === undefined) return;

    let count = 0;
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      if (date.getDay() === weekday) {
        count++;
        if (Array.isArray(value) && (value.length === 0 || value.includes(count))) {
          result.push(formatDate(date));
        }
      }
    }
  });

  return result;
}

export function getOffWeekdaysFromShift(shift: any): number[] {
  if (!shift) return [0, 6];
  const dayMap: { [key: string]: number } = {
    sun_day: 0,
    mon_day: 1,
    tues_day: 2,
    wednes_day: 3,
    thurs_day: 4,
    fri_day: 5,
    satur_day: 6,
  };

  const offWeekdays: number[] = [];
  Object.entries(dayMap).forEach(([key, weekday]) => {
    const val = shift[key];
    if (Array.isArray(val)) {
      offWeekdays.push(weekday);
    }
  });
  return offWeekdays.length > 0 ? offWeekdays : [0, 6];
}

function formatDate(date: Date): string {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function expandLeaveDates(leaves: any): any[] {
  const result: any[] = [];
  if (!Array.isArray(leaves)) return result;

  leaves.forEach((leave: any) => {
    const start = new Date(leave.start_date);
    const end = new Date(leave.end_date);

    let current = new Date(start);
    while (current <= end) {
      const yyyy = current.getFullYear();
      const mm = String(current.getMonth() + 1).padStart(2, "0");
      const dd = String(current.getDate()).padStart(2, "0");

      result.push({
        date: `${yyyy}-${mm}-${dd}`,
        data: leave,
      });

      current.setDate(current.getDate() + 1);
    }
  });

  return result;
}

export function createUnifiedArray(
  data: any,
  selectedYear: number,
  selectedMonth: number
): UnifiedEntry[] {
  const safeData = data || {};
  let result: UnifiedEntry[] = [];
  const dates = getDatesForFullMonth(+selectedMonth + 1, +selectedYear);
  const weekend = getLeaveDates(safeData.shift || {}, +selectedMonth, +selectedYear);
  const resultLeave = expandLeaveDates(safeData.leaves || []);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const attendances = Array.isArray(safeData.attendances) ? safeData.attendances : [];
  const holidays = Array.isArray(safeData.holidays) ? safeData.holidays : [];

  for (const dt of dates) {
    const currentDateObj = new Date(dt);
    currentDateObj.setHours(0, 0, 0, 0);
    const isFuture = currentDateObj.getTime() > today.getTime();

    let attn = attendances.find(
      (m: any) => m.attendance_date && moment(m.attendance_date).format("YYYY-MM-DD") === dt
    );
    let holiday = holidays.find(
      (m: any) => m.date && moment(m.date).format("YYYY-MM-DD") === dt
    );
    let wend = weekend.find((m: any) => m === dt);
    let resl = resultLeave.find((m: any) => m.date === dt);

    if (attn) {
      result.push({
        id: `attn-${dt}`,
        date: dt,
        type: "attendance",
        original: attn,
      });
    } else if (resl) {
      result.push({
        id: `leave-${dt}`,
        date: dt,
        type: "leave",
        original: resl.data,
      });
    } else if (holiday) {
      result.push({
        id: `holiday-${dt}`,
        date: dt,
        type: "holiday",
        original: holiday,
      });
    } else if (wend) {
      result.push({
        id: `weekend-${dt}`,
        date: dt,
        type: "weekend",
      });
    } else if (isFuture) {
      result.push({
        id: `future-${dt}`,
        date: dt,
        type: "future",
      });
    } else {
      result.push({
        id: `absent-${dt}`,
        date: dt,
        type: "absent",
      });
    }
  }

  return result;
}
